import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ContractorProfile {
  id: string;
  company_id: string;
  tenant_id: string;
  auth_user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  title: string | null;
  is_active: boolean;
  is_primary_contact: boolean;
  can_upload_documents: boolean;
  can_view_prequalification_status: boolean;
  last_login_at: string | null;
  company?: {
    id: string;
    name: string;
    company_type: string;
    email: string | null;
    phone: string | null;
    website: string | null;
    address_line1: string | null;
    address_line2: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    prequalification_status: string;
    prequalification_expiry_date: string | null;
  };
}

export function useContractorProfile() {
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['contractor-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('contractor_portal_users')
        .select(`
          *,
          companies (
            id,
            name,
            company_type,
            email,
            phone,
            website,
            address_line1,
            address_line2,
            city,
            state,
            zip_code,
            prequalification_status,
            prequalification_expiry_date
          )
        `)
        .eq('auth_user_id', user.id)
        .single();

      if (error) throw error;
      return data as ContractorProfile;
    },
  });

  const updateProfile = useMutation({
    mutationFn: async (data: { first_name?: string; last_name?: string; phone?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('contractor_portal_users')
        .update(data)
        .eq('auth_user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    },
  });

  const updateLastLogin = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('contractor_portal_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('auth_user_id', user.id);

      if (error) throw error;
    },
  });

  return {
    profile,
    isLoading,
    updateProfile: updateProfile.mutateAsync,
    updateLastLogin: updateLastLogin.mutateAsync,
  };
}
